import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { DfpTabsState } from './dfp-tabs.state';

/**
 * Demonstrativos adiados da Análise Econômica (card 3515, G2), tirado do `DfpComponent` para ele não passar do teto
 * de tamanho. Quando a tela abre direto na aba IR dos sócios, as ~12 chamadas da DFP (status, períodos, quatro
 * demonstrativos, subcontas, documentos de origem...) ficam para quando o usuário trocar de aba ou pedir a
 * exportação, em vez de disputar a rede e o backend com as do IR.
 *
 * Provido pelo `DfpComponent` (junto com o `DfpTabsState`); quem carrega de fato é o componente, que registra a
 * carga em `connect`.
 */
@Injectable()
export class DfpStatementsDeferral {
  private readonly tabs = inject(DfpTabsState);
  private readonly toast = inject(ToastHelperService);

  /** Demonstrativos ainda não pedidos porque a tela abriu na aba IR dos sócios. */
  readonly pending = signal(false);
  private load: (() => void) | null = null;

  constructor() {
    // Carrega quando a aba ativa deixa de ser a IR dos sócios (troca de aba, ou permissão de IR que não veio e fez a
    // tela cair em Demonstrativos).
    effect(() => {
      const active = this.tabs.active();
      if (this.pending() && active !== 'partner-income-tax') {
        untracked(() => this.flush());
      }
    });
  }

  /** Carga dos demonstrativos do componente, chamada quando o adiamento acaba. */
  connect(load: () => void): void {
    this.load = load;
  }

  /**
   * Adia a carga se a aba pedida (URL ou clique) é a IR dos sócios. Somente leitura nunca tem a aba de IR.
   * Devolve `true` quando adiou.
   */
  deferIfPartnerTabRequested(readOnly: boolean): boolean {
    if (readOnly || !this.tabs.isRequested('partner-income-tax')) return false;
    this.pending.set(true);
    return true;
  }

  /** A carga começou (adiada ou não): não há mais nada pendente. */
  markLoading(): void {
    this.pending.set(false);
  }

  /**
   * Exporta só com os demonstrativos na tela (a exportação lê as tabelas do DOM). Se ainda não carregaram, pede a
   * carga e avisa.
   */
  guardExport(isLoading: boolean, exportAction: () => void): void {
    if (this.pending() || isLoading) {
      if (this.pending()) this.flush();
      this.toast.showToastInfo(
        'Os demonstrativos ainda estão carregando',
        'Use Exportar de novo em alguns segundos, quando as tabelas aparecerem.'
      );
      return;
    }
    exportAction();
  }

  private flush(): void {
    this.pending.set(false);
    this.load?.();
  }
}
